import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Trophy, ExternalLink, Instagram, Calendar, Award, Info, Mail, Clock } from 'lucide-react';

const ConcursosView = ({ concursos }) => {
  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'abierto': return 'bg-green-100 text-green-800';
      case 'proximamente': return 'bg-blue-100 text-blue-800'; 
      case 'cerrado': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case 'abierto': return '🟢';
      case 'proximamente': return '🔵';
      case 'cerrado': return '⚫';
      default: return '⚫';
    }
  };

  const handleParticipate = (concurso) => {
    if (concurso.contacto) {
      const subject = `Consulta sobre ${concurso.titulo}`;
      const body = `Hola,\n\nEstoy interesado/a en participar en ${concurso.titulo}.\n\nPor favor, envíenme más información.\n\nGracias,`;
      const mailtoLink = `mailto:${concurso.contacto}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailtoLink;
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Concursos Carola Mía</h2>
        <p className="text-gray-600">Participa en nuestros concursos literarios y gana premios increíbles</p>
      </div>
      
      {/* Información de redes sociales */}
      <Card className="mb-6 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
        <CardContent className="py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <h3 className="text-lg font-semibold text-purple-900 mb-2 flex items-center gap-2">
                <Info className="h-5 w-5" />
                Mantente al día
              </h3>
              <p className="text-purple-800">
                Síguenos en Instagram para información actualizada sobre concursos, publicaciones y eventos especiales
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                variant="outline" 
                className="bg-white hover:bg-purple-50"
                onClick={() => window.open('https://instagram.com/carolamiaediciones', '_blank')}
              >
                <Instagram className="h-4 w-4 mr-2" />
                @carolamiaediciones
              </Button>
              <Button 
                variant="outline"
                className="bg-white hover:bg-purple-50"
                onClick={() => window.open('https://www.carolamiaediciones.es', '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Sitio Web
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {concursos?.concursos ? (
        <div className="grid gap-6 md:grid-cols-1">
          {concursos.concursos.map((concurso) => (
            <Card key={concurso.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-xl text-purple-900 mb-2">
                      {concurso.titulo}
                    </CardTitle>
                    <CardDescription className="mb-2">
                      {concurso.descripcion}
                    </CardDescription>
                  </div>
                  <Badge className={getEstadoColor(concurso.estado)}>
                    {getEstadoIcon(concurso.estado)} {concurso.estado}
                  </Badge>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {/* Fechas e información clave */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Fechas importantes
                    </h4>
                    <div className="space-y-1 text-sm text-blue-800">
                      <div>Apertura: {concurso.fecha_apertura}</div>
                      <div>Cierre: {concurso.fecha_cierre}</div>
                    </div>
                  </div>
                  
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <h4 className="font-medium text-purple-900 mb-2 flex items-center gap-2">
                      <Trophy className="h-4 w-4" />
                      Temática
                    </h4>
                    <div className="text-purple-800 font-semibold text-lg">
                      "{concurso.tematica}"
                    </div>
                  </div>
                </div>

                {/* Requisitos */}
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-3">📋 Requisitos</h4>
                  <ul className="space-y-1 text-sm text-gray-700">
                    {concurso.requisitos?.map((requisito, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-purple-600 mt-1">•</span>
                        <span>{requisito}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Reconocimiento */}
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <h4 className="font-medium text-yellow-900 mb-2 flex items-center gap-2">
                    <Award className="h-4 w-4" />
                    Reconocimiento
                  </h4>
                  <p className="text-yellow-800 font-semibold mb-2">{concurso.reconocimiento}</p>
                  {concurso.observaciones && (
                    <p className="text-yellow-700 text-sm italic">{concurso.observaciones}</p>
                  )}
                </div>

                {/* Contacto y enlaces */}
                <div className="grid md:grid-cols-3 gap-3">
                  <Button 
                    variant="outline"
                    className="bg-white hover:bg-red-50 border-red-200"
                    onClick={() => handleParticipate(concurso)}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Contacto
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="bg-white hover:bg-pink-50 border-pink-200"
                    onClick={() => window.open(`https://instagram.com/${concurso.enlaces?.instagram?.replace('@', '') || 'carolamiaediciones'}`, '_blank')}
                  >
                    <Instagram className="h-4 w-4 mr-2" />
                    Instagram
                  </Button>
                  
                  <Button 
                    variant="outline"
                    className="bg-white hover:bg-blue-50 border-blue-200"
                    onClick={() => window.open(`https://${concurso.enlaces?.web || 'www.carolamiaediciones.es'}`, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Web
                  </Button>
                </div>

                {/* Información adicional */}
                {concurso.bases_completas && (
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <p className="text-blue-800 text-sm flex items-center gap-2">
                      <Info className="h-4 w-4" />
                      {concurso.bases_completas}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="text-center py-8">
            <Trophy className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold mb-2">Cargando concursos...</h3>
            <p className="text-gray-600">Preparando información sobre nuestros concursos actuales</p>
          </CardContent>
        </Card>
      )}
      
      {/* Información adicional */}
      <Card className="mt-6 bg-amber-50 border-amber-200">
        <CardHeader>
          <CardTitle className="text-amber-900">💡 Consejos para participar</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h4 className="font-medium text-amber-900 mb-2">📋 Antes de enviar</h4>
              <ul className="text-sm text-amber-800 space-y-1">
                <li>• Lee atentamente las bases del concurso</li>
                <li>• Revisa la extensión máxima permitida</li>
                <li>• Corrige ortografía y gramática</li>
                <li>• Respeta la fecha límite de entrega</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-amber-900 mb-2">✨ Para destacar</h4>
              <ul className="text-sm text-amber-800 space-y-1">
                <li>• Sé original y auténtico</li>
                <li>• Cuida la estructura narrativa</li>
                <li>• Usa un lenguaje rico pero claro</li>
                <li>• Revisa que tu historia sea coherente</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConcursosView;